import { CANVAS_WIDTH, CANVAS_HEIGHT, PLAYABLE_AREA, WALL_THICKNESS } from './constants.js';
import { lerp } from './utils.js';
import { CameraMode, CameraMotionMode } from '../shared/sharedTypes.js';
import * as PIXI from 'pixi.js';

const SCROLL_TO_BOTTOM_ON_START = true;

export default class ViewportManager {
    app: PIXI.Application;
    gameWorld: PIXI.Container;
    scale: number;
    scrollPosition: number;
    inputManager: any;
    lastTouchY: number;
    isTouching: boolean;
    ballManager: any;
    cameraMode: CameraMode;
    cameraMotionMode: CameraMotionMode;
    spectateAfterFinished: boolean;

    waitForInitialScrollToFinish: boolean;
    lastPlayerScrollTime: number;
    private initialScrollTimeoutId: number | null = null;

    constructor(app, gameWorld) {
        this.app = app;
        this.gameWorld = gameWorld;
        this.scale = 1;
        this.scrollPosition = 0;
        this.inputManager = null;
        this.lastTouchY = 0;
        this.isTouching = false;
        this.init();
        this.ballManager = undefined;
        this.cameraMode = CameraMode.KEEP_BALL_IN_VIEW;
        this.cameraMotionMode = CameraMotionMode.LERP;
        this.spectateAfterFinished = false;
        this.waitForInitialScrollToFinish = true;
        this.lastPlayerScrollTime = 0;

        window.scrollTo(0, 1);
    }

    init() {
        this.adjustViewport();
        this.setupEventListeners();
        this.scheduleInitialScroll();
        setTimeout(() => {
            this.waitForInitialScrollToFinish = false;
        }, 4000);
        window.addEventListener('click', () => {
            this.waitForInitialScrollToFinish = false;
        });
        window.addEventListener('touchstart', () => {
            this.waitForInitialScrollToFinish = false;
        });
    }

    private scheduleInitialScroll() {
        if (SCROLL_TO_BOTTOM_ON_START) {
            // Clear any existing initial scroll timeout
            if (this.initialScrollTimeoutId !== null) {
                clearTimeout(this.initialScrollTimeoutId);
            }
            // Schedule initial scroll using current CANVAS_HEIGHT (dynamically computed)
            this.initialScrollTimeoutId = window.setTimeout(
                () => {
                    const targetY = CANVAS_HEIGHT - this.getVisibleHeight();
                    this.lerpViewportScrollPositionTo(targetY, 0.03, 3000);
                    this.initialScrollTimeoutId = null;
                },
                400
            );
        }
    }

    /**
     * Recalculate initial scroll after canvas height changes (e.g., snakeup mode doubles height)
     * This should be called after game mode initialization if canvas height changed
     */
    public recalculateInitialScroll(): void {
        // Only recalculate if we haven't finished the initial scroll yet
        if (this.waitForInitialScrollToFinish && SCROLL_TO_BOTTOM_ON_START) {
            // Cancel the old scroll if it's still pending
            if (this.initialScrollTimeoutId !== null) {
                clearTimeout(this.initialScrollTimeoutId);
                this.initialScrollTimeoutId = null;
            }
            // Schedule new initial scroll with updated canvas height
            this.scheduleInitialScroll();
        }
    }

    adjustViewport() {
        const viewportWidth = window.innerWidth;
        const scaleFactor = viewportWidth < PLAYABLE_AREA ? viewportWidth / PLAYABLE_AREA : 1;
        this.scale = scaleFactor;

        this.app.renderer.resize(viewportWidth, window.innerHeight);

        if (this.gameWorld) {
            this.gameWorld.scale.set(this.scale);
            const offsetX = (viewportWidth - PLAYABLE_AREA * this.scale) / 2;
            this.gameWorld.x = offsetX - WALL_THICKNESS * this.scale;
            const offsetY = (window.innerHeight - CANVAS_HEIGHT * this.scale) / 2;
            // Clamp scroll position to new canvas height bounds
            const maxScroll = CANVAS_HEIGHT - this.getVisibleHeight();
            this.scrollPosition = Math.max(0, Math.min(this.scrollPosition, maxScroll));
            // Set initial position (scrolling methods will update this based on scrollPosition)
            this.gameWorld.y = offsetY;
        }
    }

    lerpViewportScrollPositionTo(targetY: number, speed: number, timeout?: number) {
        const maxScroll = CANVAS_HEIGHT - this.getVisibleHeight();
        targetY = Math.max(0, Math.min(targetY, maxScroll));

        let startTime = Date.now();
        let isScrolling = true;

        const animate = () => {
            if (!isScrolling) return;

            const currentTime = Date.now();
            if (timeout !== undefined && currentTime - startTime > timeout) {
                isScrolling = false;
                return;
            }

            this.scrollPosition = lerp(this.scrollPosition, targetY, speed);
            this.scrollPosition = Math.max(0, Math.min(this.scrollPosition, maxScroll));
            const newY = -this.scrollPosition * this.scale;
            this.gameWorld.y = Math.max(-maxScroll * this.scale, Math.min(0, newY));
            if (this.inputManager) {
                this.inputManager.updateMousePositionRelativeToViewport();
            }

            if (Math.abs(this.scrollPosition - targetY) > 0.1 && this.scrollPosition < maxScroll) {
                requestAnimationFrame(animate);
            } else {
                isScrolling = false;
            }
        };

        animate();

        const cancelScroll = () => {
            isScrolling = false;
        };

        window.addEventListener('touchstart', cancelScroll, { once: true });
        window.addEventListener('wheel', cancelScroll, { once: true });
        window.addEventListener('mousewheel', cancelScroll, { once: true });
    }

    setupEventListeners() {
        window.addEventListener('resize', this.adjustViewport.bind(this));
        window.addEventListener('orientationchange', this.adjustViewport.bind(this));
        window.addEventListener('load', () => {
            setTimeout(() => {
                this.adjustViewport();
            }, 0);
        });
    }

    update() {
        // Don't update viewport if campaign UI is visible
        const campaignUI = document.getElementById('campaign-ui');
        if (campaignUI && campaignUI.parentElement) {
            return;
        }

        if (this.waitForInitialScrollToFinish || (this.lastPlayerScrollTime + 1000 > Date.now())) {
            return;
        }
        if (!this.ballManager.inMotion) {
            return;
        }
        const ballPosition = this.ballManager.getPosition();
        if (ballPosition) {
            // Calculate dynamic lerp speed based on ball velocity and distance from center
            let dynamicLerpSpeed = 0.03; // Default speed

            if (this.cameraMotionMode === CameraMotionMode.LERP && this.ballManager.ball) {
                // Get ball speed (velocity magnitude)
                const ballVelocity = this.ballManager.ball.velocity;
                const ballSpeed = Math.sqrt(ballVelocity.x ** 2 + ballVelocity.y ** 2);

                // Base speed adjustment based on ball speed (faster ball = faster camera)
                const speedMultiplier = Math.min(ballSpeed * 0.005, 0.5); // Cap at 0.5 for very fast balls

                // Calculate distance from screen center for recovery bias
                const screenY = this.getScreenYFromWorldY(ballPosition.y);
                const screenCenterY = this.getVisibleHeight() / 2;
                const distanceFromCenter = Math.abs(screenY - screenCenterY);
                const maxDistance = this.getVisibleHeight() / 2; // Maximum possible distance from center
                const distanceRatio = distanceFromCenter / maxDistance; // 0 to 1, where 1 means ball is at screen edge

                // Recovery bias: further from center = faster lerping (up to 3x faster)
                const recoveryMultiplier = 1 + (distanceRatio * 2);

                // Combine speed and recovery factors
                dynamicLerpSpeed = 0.03 * (1 + speedMultiplier) * recoveryMultiplier;

                // Clamp to reasonable range (don't go too fast or too slow)
                dynamicLerpSpeed = Math.max(0.02, Math.min(dynamicLerpSpeed, 0.15));
            }

            switch (this.cameraMode) {
                case CameraMode.CENTER_BALL:
                    this.lerpViewportScrollPositionTo(
                        ballPosition.y - this.getVisibleHeight() / 2,
                        this.cameraMotionMode === CameraMotionMode.LERP ? dynamicLerpSpeed : 1
                    );
                    break;
                case CameraMode.KEEP_BALL_IN_VIEW:
                    const screenY = this.getScreenYFromWorldY(ballPosition.y);
                    const visibleHeight = this.getVisibleHeight();
                    const lowerLimit = visibleHeight * 0.6;
                    const upperLimit = visibleHeight * 0.4;
                    let target = this.scrollPosition; // Start with current position

                    if (screenY > visibleHeight - lowerLimit) {
                        target = ballPosition.y - visibleHeight + lowerLimit;
                    } else if (screenY < upperLimit) {
                        target = ballPosition.y - upperLimit;
                    }

                    // Clamp the target to the game world boundaries
                    const maxScroll = CANVAS_HEIGHT - visibleHeight;
                    target = Math.max(0, Math.min(target, maxScroll));

                    this.lerpViewportScrollPositionTo(target, this.cameraMotionMode === CameraMotionMode.LERP ? dynamicLerpSpeed : 1);
                    break;
                case CameraMode.FREE:
                    break;
                case CameraMode.SPECTATOR:
                    break;
            }
        }
    }

    updateScrollPosition(deltaY) {
        this.lastPlayerScrollTime = Date.now();
        const prevScrollPosition = this.scrollPosition;
        this.scrollPosition += (deltaY * 0.4) / this.scale;

        const maxScroll = CANVAS_HEIGHT - this.getVisibleHeight();
        const minScroll = 0;
        this.scrollPosition = Math.max(minScroll, Math.min(this.scrollPosition, maxScroll));

        if (this.scrollPosition !== prevScrollPosition) {
            this.gameWorld.y = -this.scrollPosition * this.scale;
        }

        return this.scrollPosition !== prevScrollPosition;
    }

    getWorldYFromScreenY(screenY) {
        return (screenY / this.scale) + this.scrollPosition;
    }

    getWorldXFromScreenX(screenX) {
        return screenX / this.scale - (this.gameWorld ? this.gameWorld.x / this.scale : 0);
    }

    getScreenXFromWorldX(worldX) {
        return (worldX + (this.gameWorld ? this.gameWorld.x / this.scale : 0)) * this.scale;
    }

    getScreenYFromWorldY(worldY) {
        return (worldY - this.scrollPosition) * this.scale;
    }

    getVisibleHeight() {
        return window.innerHeight / this.scale;
    }

    public setCameraMode(mode: CameraMode) {
        this.cameraMode = mode;
    }

    public setCameraMotionMode(mode: CameraMotionMode) {
        this.cameraMotionMode = mode;
    }

    public setSpectateAfterFinished(value: boolean): void {
        this.spectateAfterFinished = value;
    }
}